<template>
	<track-wrap v-if="prioItem" :track="track" :state="effectiveTrackState" :last="trackLast" :hollow="isTrackHollow"
				:muted="isTrackMuted"
				:link-prev="trackLinkPrev" :link-next="trackLinkNext">
		<v-btn class="btn-prio" :class="componentClass" icon
			   :small="small || isSubcontent"
			   :large="large"
			   :disabled="disabled"
			   :title="title"
			   @click.stop.prevent="onClick">
			<loader v-if="isEditorPending||loading" small/>
			<!--		<v-icon v-else-if="!hasSomeAccessToTask(item)"
							class="btn-prio__icon"
							:small="small"
							:large="large">mdi-lock-outline
					</v-icon>-->
			<v-icon v-else
					class="btn-prio__icon"
					:color="iconColor"
					:small="(small||isSubcontent)&&!xSmall"
					:x-small="xSmall"
					:large="large">{{ icon }}
			</v-icon>
			<span v-if="label" class="ml-2 btn-prio__label">{{ label }}</span>
		</v-btn>
	</track-wrap>
</template>

<script>
import vp from "@/mixins/vp";
import base from "@/mixins/base";
import {
	prios,
	isTaskOverdue, isTaskDeleted, isTaskNote,
	colors, prioColorOf, prioIconOf, PRIOS,
} from "@/utils/task";
import {flatArray} from "@/utils/utils";
import {posParent} from "@/utils/pos";

/**
 * Обёртка кнопки для отрисовки элемента трека процесса (линия + кружок).
 * Без track ничего не добавляет - возвращает саму кнопку.
 */
const TrackWrap = {
	functional: true,
	props: {
		track: {type: String, default: null},
		state: {type: String, default: "upcoming"},
		last: {type: Boolean, default: false},
		hollow: {type: Boolean, default: false},
		muted: {type: Boolean, default: false},
		linkPrev: {type: Boolean, default: false},
		linkNext: {type: Boolean, default: false},
	},
	render(h, ctx) {
		const children = ctx.children || [];
		const {track, state, last, hollow, muted, linkPrev, linkNext} = ctx.props;
		if (!track) return children.length === 1 ? children[0] : children;
		return h("div", {
			class: ["btn-prio-track", "--" + track, "--" + state, {
				"--last": last,
				"--hollow": hollow,
				"--muted": muted,
				"--link-prev": linkPrev,
				"--link-next": linkNext
			}],
		}, children);
	}
};

export default {
	name: "btn-prio",
	mixins: [vp, base],
	components: {TrackWrap},
	props: {
		item: {
			type: Object,
		},
		label: {
			type: String,
			default: null
		},
		small: {
			type: Boolean,
			default: false
		},
		large: {
			type: Boolean,
			default: false
		},
		readonly: {
			type: Boolean,
			default: false
		},
		callback: {
			type: Function,
		},
		isSubcontent: {
			type: Boolean,
			default: false
		},
		stage: {
			type: Boolean,
			default: false
		},
		loading: {
			type: Boolean,
			default: false
		},
		color: {
			type: String,
			default: null
		},
		/**
		 * Если задан - выбор приоритета отдаётся этому обработчику, а task/update не вызывается
		 * (используется для черновика новой задачи, которой ещё нет на сервере).
		 */
		handler: {
			type: Function,
			default: undefined
		},
		/**
		 * Полный (нефильтрованный) список задач, среди которых находится item.
		 * Передается в prio-menu (нужен для subButtons[].appliesTo).
		 */
		siblings: {
			type: Array,
			default: null
		},
		/**
		 * Задача, которую завершает пункт "Готово" вместо item (например, активный шаг процесса).
		 * Остальные пункты меню по-прежнему применяются к самому item.
		 */
		doneItem: {
			type: Object,
			default: null
		},
		/** Значения prio, пункты которых скрываются в меню (например, [PRIOS.COMMENT, PRIOS.HIDDEN]) */
		excludePrios: {
			type: Array,
			default: null
		},
		/** Не показывать в меню дополнительные кнопки пунктов (например, "наверх"); при заданном track скрыты всегда */
		noSubButtons: {
			type: Boolean,
			default: false
		},
		/**
		 * Элемент трека процесса, который рисуется вместе с кнопкой:
		 * "step" - шаг (линия + кружок), "goal" - цель процесса (пустой кружок), "line" - только линия.
		 * Без значения трек не выводится.
		 */
		track: {
			type: String,
			default: null,
			validator: v => ["step", "goal", "line"].includes(v)
		},
		/**
		 * Состояние элемента трека: upcoming - впереди, passed - пройден (линия зелёная),
		 * current - текущий шаг. Выполненность задачи (done / doneAhead) компонент определяет сам, см. effectiveTrackState.
		 */
		trackState: {
			type: String,
			default: "upcoming",
			validator: v => ["upcoming", "passed", "current"].includes(v)
		},
		/**
		 * Соседние шаги (сверху / снизу) тоже выполнены заранее (doneAhead) - линия между их кружками зелёная.
		 * Учитывается только для состояния doneAhead.
		 */
		trackLinkPrev: {
			type: Boolean,
			default: false
		},
		trackLinkNext: {
			type: Boolean,
			default: false
		},
		/** Последний элемент трека - линия заканчивается на кружке */
		trackLast: {
			type: Boolean,
			default: false
		}
	},
	data: () => ({}),
	computed: {
		xSmall() {
			return ["done", "doneAhead"].includes(this.effectiveTrackState);// || (!!this.track && this.isTaskDeleted(this.item));
		},
		disabled() {
			return this.isAnon || this.readonly;// || !this.hasFullAccessToTask(this.item);
		},
		prioItem() {
			return prios.find(el => el.appliesTo(this.item, this));
		},
		isEditorPending() {
			return this.item.id === this.task.item?.id && this.$store.state.ui.pending.editor;
		},
		/** Удалённая задача, заметка или папка в треке - кружок с рамкой, как у цели процесса */
		isTrackHollow() {
			return !!this.track && !this.isTrackMuted && (isTaskDeleted(this.item) || isTaskNote(this.item) || !!this.item?.isFolder);
		},
		/** Пройденная удалённая задача: серые линия и кружок обычного для шага размера */
		isTrackMuted() {
			return this.track === "step" && isTaskDeleted(this.item) && ["passed", "done", "doneAhead"].includes(this.effectiveTrackState);
		},
		/**
		 * Состояние трека с учётом выполненности задачи: выполненный шаг - done (пройден) или doneAhead
		 * (выполнен заранее, шаг ещё впереди: кружок как у done, линия серая). Остальные - как в trackState.
		 */
		effectiveTrackState() {
			// выполненная цель (сам процесс отмечен выполненным) - зелёный кружок с белым флагом
			if (this.track === "goal" && this.isTaskDone(this.item)) return "goalDone";
			if (this.track !== "step" || !this.isTaskDone(this.item)) return this.trackState;
			if (this.trackState === "passed") return "done";
			if (this.trackState === "upcoming") return "doneAhead";
			return this.trackState;
		},
		icon() {
			//if (this.$store.state.ui.pending.editor) return "mdi-";
			if (this.track === "goal") return "mdi-flag-checkered";
			// в треке обычная и важная задача рисуются пустыми - на месте иконки остаётся кружок трека
			if (this.track && ([PRIOS.NORMAL, PRIOS.IMPORTANT].includes(this.prioItem?.prio) || this.isTaskDone(this.item))) return null;
			// неактивный шаг ("Не активно", иконка минус) в треке тоже рисуется пустым - кружок трека и так показывает его состояние
			if (this.track && this.prioItem?.icon === "mdi-minus") return null;
			return prioIconOf(this.item, this);
			/*if (typeof this.prioItem.icon == "function") return this.prioItem.icon(this.item, this);
			return this.prioItem?.icon;*/
		},
		prioItemLabel() {
			return typeof this.prioItem.label == "function" ? this.prioItem.label(this.item, this) : this.prioItem.label;
		},
		iconColor() {
			if (this.color) return this.color;
			if (this.track === "goal") {
				if (this.effectiveTrackState === "goalDone") return "white";
				return this.trackState === "passed" ? "success" : "grey";
			}
			// иконка "выполнено" лежит на зелёном кружке трека
			if (this.track && this.isTaskDone(this.item)) return "white";
			if (this.isTaskTemplate(this.item) && this.item.prio) return "grey lighten-1";
			return prioColorOf(this.item, this);
			/*if (typeof this.prioItem.color == "function") return this.prioItem.color(this.item, this);
			return this.prioItem?.color;*/
		},
		title() {
			return this.prioItem?.title || this.prioItemLabel;
		},
		componentClass() {
			const c = [];
			if (this.label) c.push("btn-prio--with-label");
			return c.length ? c : null;
		}
	},
	methods: {
		async onClick(event) {
			if (this.disabled) return;
			event.preventDefault();

			//console.log("Clicked prio", this.prioItem);

			const handler = this.handler;
			/*if (this.isSubcontent) {
				handler = (item) => {
					//console.log("Custom logic!", item);
					this.item.prio = item.prio;
					this.item.isNote = item.prio === PRIOS.NOTE;
				}
			}*/

			await this.$store.dispatch("ui/prioMenu", {
				event,
				item: this.item,
				isSubcontent: this.isSubcontent,
				siblings: this.siblings,
				doneItem: this.doneItem,
				excludePrios: this.excludePrios,
				// у шагов процесса порядок задаёт процесс - кнопки "наверх" не нужны
				noSubButtons: this.noSubButtons || !!this.track,
				//skipTaskUpdate: this.skipTaskUpdate,
				handler
			});
			/*if (typeof this.prioItem.onClick == "function") {
				this.prioItem.onClick(this.item, event);
			} else {
				await this.$store.dispatch("ui/prioMenu", {
					event,
					item: this.item,
				});
			}*/
		},
	},
}
</script>

<style lang="scss">
/** Трек процесса: колонка во всю высоту строки, кнопка приоритета сверху, линия проходит по её центру под кнопкой */
.btn-prio-track {
	position: relative;
	display: flex;
	align-items: flex-start;
	align-self: stretch;
	padding-top: 6px;

	.btn-prio {
		position: relative;
		z-index: 1;
	}

	&::before {
		content: "";
		position: absolute;
		z-index: 0;
		left: 50%;
		top: 0;
		transform: translateX(-50%);
		width: 1px;
		height: 100%;
		background: map-get($grey, "lighten-2");
		pointer-events: none;
	}

	&::after {
		content: "";
		position: absolute;
		z-index: 0;
		left: 50%;
		top: 15px;
		transform: translateX(-50%);
		border-radius: 50%;
		width: 5px;
		height: 5px;
		background: map-get($grey, "lighten-2");
		pointer-events: none;
	}

	&.--last::before {
		height: 16px;
	}

	&.--done, &.--doneAhead {
		&::before, &::after {
			background: var(--v-success-base, #4caf50);
		}
		&:not(.--muted)::after {
			top: 13px;
			width: 11px;
			height: 11px;
		}
	}

	/**
	 * Выполнена заранее: кружок зелёный, линия серая; зелёная только между кружками соседних
	 * выполненных заранее шагов (от центра кружка вверх / вниз)
	 */
	&.--doneAhead {
		--link-top: #{map-get($grey, "lighten-2")};
		--link-bottom: #{map-get($grey, "lighten-2")};

		&.--link-prev {
			--link-top: var(--v-success-base, #4caf50);
		}

		&.--link-next {
			--link-bottom: var(--v-success-base, #4caf50);
		}

		&::before {
			// 18px - центр кружка (top 13px + 11px / 2)
			background: linear-gradient(to bottom, var(--link-top) 18px, var(--link-bottom) 18px);
		}
	}

	/** Пройденная удалённая задача: серые линия и кружок обычного для шага размера */
	&.--muted {
		&::after {
			background: map-get($grey, "lighten-1");
		}
	}

	&.--current {
		&::before {
			//width: 1px;
			// зелёная часть до кружка текущего шага, ниже - серая линия к следующему шагу
			background: linear-gradient(to bottom, var(--v-success-base, #4caf50) 16px, map-get($grey, "lighten-2") 16px);
		}

		&::after {
			top: 13px;
			width: 11px;
			height: 11px;
			border: 2px solid var(--v-success-base, #4caf50);
			background: white;
		}
	}

	/** Удалённая задача, заметка или папка в треке - кружок с рамкой, как у цели процесса */
	&.--hollow {
		&::after {
			top: 10px;
			width: 16px;
			height: 16px;
			//border-color: map-get($grey, "lighten-2");
			background: map-get($grey, "lighten-4");
			//background: unset;
			//background: white;
		}

		&.--passed::after, &.--done::after {
			border-color: var(--v-success-base, #4caf50);
		}
	}

	/** Цель процесса: пустой кружок на конце линии, зелёный - если цель достигнута */
	&.--goal {
		&::after {
			top: 6px;
			width: 24px;
			height: 24px;
			border: 1px solid map-get($grey, "lighten-2");
			//background: map-get($grey, "lighten-4");
			//background: unset;
			background: white;
		}

		&.--passed::after, &.--done::after, &.--goalDone::after {
			border-color: var(--v-success-base, #4caf50);
		}

		/** Цель выполнена: зелёный кружок с белым флагом */
		&.--goalDone::after {
			background: var(--v-success-base, #4caf50);
		}
	}

	/** Отрезок линии без кружка - проходит через строку "новая задача" к цели процесса */
	&.--line::after {
		display: none;
	}
}

.btn-prio {
	display: inline;
	flex-shrink: 0;
	flex-grow: 0;
	/*width: 34px !important;
	height: 34px !important;*/
	padding: 0;

	.loader {
		width: 16px !important;;
		height: 16px !important;
		font-size: 16px !important;;
	}

	/*&.v-size--small {
		width: 24px !important;
		height: 24px !important;
	}*/

	&__icon {
		//opacity: 1; //$default-icon-opacity;
		//color: red;
		width: 26px;
		height: 26px;
		position: relative;
	}

	&--with-label {
		display: flex;
		align-items: center;
		width: auto;
	}

	&__label {
		flex: 1;
		white-space: nowrap;
		cursor: pointer;

		&:hover {
			opacity: $default-hover-opacity;
		}
	}
}
</style>